<script setup lang="ts">
import type { MeOrgPosition } from '~/composables/equipe/useMyOrgPositions'
import type { LayoutAuthMode } from '~/composables/layout/useAppLayoutShell'
import type { SeasonalDecorPrefs } from '~/composables/ui/useSeasonalTheme'
import { useDefaultLayout } from '~/composables/layout/useDefaultLayout'
import { useOrganigrammeEditRouteAccess } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import AppLayoutHeader from '~/components/app/layout/AppLayoutHeader.vue'
import AppLayoutFooter from '~/components/app/layout/AppLayoutFooter.vue'
import AppLayoutMain from '~/components/app/layout/AppLayoutMain.vue'
import AppLayoutOverlayHost from '~/components/app/layout/AppLayoutOverlayHost.vue'
import AppLayoutToolboxHost from '~/components/app/layout/AppLayoutToolboxHost.vue'
import DevOidcOverrideBanner from '~/components/app/layout/DevOidcOverrideBanner.vue'

const props = withDefaults(defineProps<{ authMode?: LayoutAuthMode }>(), {
  authMode: 'auto',
})

const {
  loggedIn,
  shell,
  profile,
  editMode,
  keyboard,
  toolbox,
  overlays,
} = useDefaultLayout({ authMode: props.authMode })

const { directionAdminDirLibs, canEditOrganigramOnCurrentView } = useOrganigrammeEditRouteAccess()

const headerComponentRef = ref<InstanceType<typeof AppLayoutHeader> | null>(null)

watch(
  () => headerComponentRef.value?.globalSearchBarRef,
  (searchBarRef) => {
    keyboard.globalSearchBarRef = searchBarRef ?? null
  },
  { immediate: true },
)

function onGeolocateEmptyUpdate(value: boolean): void {
  toolbox.showGeolocateEmptyModal = value
}

function onGeolocateErrorUpdate(value: boolean): void {
  toolbox.showGeolocateErrorModal = value
}

function onGeolocateChoiceUpdate(value: boolean): void {
  toolbox.showGeolocateChoiceModal = value
}

function onGeolocateChoicePick(choice: MeOrgPosition): void {
  toolbox.onGeolocateChoicePick(choice)
}

function onSeasonalWelcomeUpdate(value: boolean): void {
  overlays.showSeasonalWelcome = value
}

function onSeasonalWelcomeConfirm(payload: { suppressUntilNextEvent: boolean; prefs: SeasonalDecorPrefs }): void {
  overlays.onSeasonalWelcomeConfirm(payload)
}

function onTrainingCenterUpdate(value: boolean): void {
  overlays.showTrainingCenter = value
}

function onToolboxOpenUpdate(value: boolean): void {
  toolbox.toolboxOpen = value
}

function onOrganizationTreeModalOpenUpdate(value: boolean): void {
  toolbox.organizationTreeModalOpen = value
}
</script>

<template>
  <div class="app-layout">
    <DevOidcOverrideBanner
      v-if="shell.showDevOidcProfilesBar"
      :dev-oidc-override-banner="shell.devOidcOverrideBanner"
    />
    <AppLayoutHeader
      ref="headerComponentRef"
      :logged-in="loggedIn"
      :can-edit-organigram-on-current-view="canEditOrganigramOnCurrentView"
      :is-editing="editMode.isOrganigrammeEditing"
      :edit-mode-shortcut-deactivate-hint="editMode.editModeShortcutDeactivateHint"
      :full-name="profile.fullName"
      :user-email="profile.userEmail"
      :my-nni="profile.myNni ?? undefined"
      :my-avatar-display-mode="profile.myAvatarDisplayMode"
      :my-avatar-config="profile.myAvatarConfig"
      :my-graph-photo-url="profile.myGraphPhotoUrl"
      :profile-role-badges="profile.profileRoleBadges"
      :department-attachment="profile.departmentAttachment"
      :first-login-display-french="profile.firstLoginDisplayFrench"
      :profile-rights-labels="profile.profileRightsLabels"
      :user-can-access-admin="profile.userCanAccessAdmin"
      :direction-admin-dir-libs="directionAdminDirLibs"
    />
    <AppLayoutOverlayHost
      :show-geolocate-empty-modal="toolbox.showGeolocateEmptyModal"
      :show-geolocate-error-modal="toolbox.showGeolocateErrorModal"
      :show-geolocate-choice-modal="toolbox.showGeolocateChoiceModal"
      :geolocate-choices="toolbox.geolocateChoices"
      :geolocate-error-message="toolbox.geolocateErrorMessage"
      :show-beta-notice="overlays.showBetaNotice"
      :show-seasonal-welcome="overlays.showSeasonalWelcome"
      :active-seasonal-event="overlays.activeSeasonalEvent"
      :decor-prefs="overlays.decorPrefs"
      :show-training-center="overlays.showTrainingCenter"
      :available-trainings="overlays.availableTrainings"
      :launch-blocker-reason="overlays.launchBlockerReason"
      :should-show-avatar-mode-gate="overlays.shouldShowAvatarModeGate"
      :is-saving-avatar-mode="overlays.isSavingAvatarMode"
      :full-name="profile.fullName"
      :my-graph-photo-url="profile.myGraphPhotoUrl"
      @update:show-geolocate-empty="onGeolocateEmptyUpdate"
      @update:show-geolocate-error="onGeolocateErrorUpdate"
      @update:show-geolocate-choice="onGeolocateChoiceUpdate"
      @close-geolocate-choice="toolbox.closeGeolocateChoiceModal()"
      @geolocate-choice-pick="onGeolocateChoicePick"
      @acknowledge-beta-notice="overlays.acknowledgeBetaNotice()"
      @update:show-seasonal-welcome="onSeasonalWelcomeUpdate"
      @seasonal-welcome-confirm="onSeasonalWelcomeConfirm"
      @live-seasonal-decor-updates="overlays.applyLiveSeasonalDecorUpdates($event)"
      @update:show-training-center="onTrainingCenterUpdate"
      @start-training="overlays.onStartTraining($event)"
      @confirm-avatar-mode="overlays.onConfirmAvatarMode($event)"
    />
    <AppLayoutMain
      :should-use-auth-gate="shell.shouldUseAuthGate"
      :show-back-to-national-view="shell.showBackToNationalView"
      :is-loading-prefs="overlays.isLoadingPrefs"
      :should-show-welcome-splash="overlays.shouldShowWelcomeSplash"
      :is-welcome-resolved="overlays.isWelcomeResolved"
      @welcome-splash-finished="overlays.onWelcomeSplashFinished()"
    >
      <slot />
    </AppLayoutMain>
    <AppLayoutFooter
      :show-seasonal-footer-link="overlays.showSeasonalFooterLink"
      :seasonal-footer-short-label="overlays.seasonalFooterShortLabel"
      :show-onboarding-restart-button="overlays.showOnboardingRestartButton"
      :support-contact-mailto-href="overlays.supportContactMailtoHref"
      @open-seasonal-welcome="overlays.openSeasonalWelcomeModal()"
      @open-training-center="overlays.onOpenTrainingCenter()"
    />
    <AppLayoutToolboxHost
      :show-toolbox="toolbox.showToolbox"
      :logged-in="loggedIn"
      :can-edit-organigram-on-current-view="canEditOrganigramOnCurrentView"
      :is-editing="editMode.isEditing"
      :geolocate-loading="toolbox.geolocateLoading"
      :edit-mode-shortcut-keys="keyboard.editModeShortcutKeys"
      :edit-mode-pressed-keys="keyboard.editModePressedKeys"
      :edit-mode-sublimation="keyboard.sublimationTriggered"
      :toolbox-shortcut-keys="keyboard.toolboxShortcutKeys"
      :toolbox-pressed-keys="keyboard.toolboxShortcutPressedKeys"
      :toolbox-sublimation="keyboard.toolboxShortcutSublimation"
      :geoloc-shortcut-keys="keyboard.geolocShortcutKeys"
      :geoloc-pressed-keys="keyboard.geolocShortcutPressedKeys"
      :geoloc-sublimation="keyboard.geolocShortcutSublimation"
      :toolbox-open="toolbox.toolboxOpen"
      :organization-tree-modal-open="toolbox.organizationTreeModalOpen"
      :can-use-organization-tree="toolbox.canUseOrganizationTree"
      @update:toolbox-open="onToolboxOpenUpdate"
      @geolocate="toolbox.onToolboxGeolocate()"
      @open-tree="toolbox.onToolboxOpenTree()"
      @update:is-editing="toolbox.onToolboxIsEditingUpdate($event)"
      @update:organization-tree-modal-open="onOrganizationTreeModalOpenUpdate"
    />
  </div>
</template>

<style lang="scss">
@use "~/assets/scss/partials/layouts/app-layout-shell";
</style>
